


















































import {
  defineComponent,
  PropType,
  ref,
  computed,
  inject,
  ComputedRef,
} from '@vue/composition-api';
import {
  Word,
  ItemPropertyRow,
  InsertItemPropertyRow,
  AppliedProperty,
  PreselectionProperty,
} from '@oare/types';
import WordGrammar from './WordGrammar/WordGrammar.vue';
import { ReloadKey } from '../../../index.vue';
import sl from '@/serviceLocator';
import PropertiesTree from '@/components/Properties/PropertiesTree.vue';
import { convertAppliedPropsToItemProps } from '@oare/oare';

export default defineComponent({
  name: 'AddFormDialog',
  components: {
    WordGrammar,
    PropertiesTree,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    word: {
      type: Object as PropType<Word>,
      required: true,
    },
  },
  setup({ word }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const reload = inject(ReloadKey);

    const addFormLoading = ref(false);
    const formComplete = ref(false);
    const newFormSpelling = ref('');
    const properties = ref<AppliedProperty[]>([]);

    const setProperties = (propertyList: AppliedProperty[]) => {
      properties.value = propertyList;
    };

    const formAlreadyExists = computed(() => {
      const formsWithSameSpelling = word.forms.filter(
        form => form.form === newFormSpelling.value
      );

      if (formsWithSameSpelling.length === 0) {
        return false;
      }

      const selectedItemProperties = formsWithSameSpelling.map(form =>
        convertAppliedPropsToItemProps(properties.value, form.uuid)
      );

      return formsWithSameSpelling.some(
        (existing, idx) =>
          existing.properties.length === selectedItemProperties[idx].length &&
          existing.properties.every(prop =>
            hasMatchingProperty(selectedItemProperties[idx], prop)
          )
      );
    });

    const hasMatchingProperty = (
      comparison: InsertItemPropertyRow[],
      property: ItemPropertyRow
    ) => {
      return comparison.some(
        prop =>
          prop.variableUuid === property.variableUuid &&
          prop.valueUuid === property.valueUuid &&
          prop.level === property.level
      );
    };

    const partOfSpeechPreselects: ComputedRef<PreselectionProperty[]> =
      computed(() => {
        const posProperties = word.properties
          .filter(
            prop => prop.variableName === 'Part of Speech' && prop.valueUuid
          )
          .sort((a, b) => {
            if (!a.level) {
              return -1;
            } else if (!b.level) {
              return 1;
            }
            return a.level - b.level;
          });
        return posProperties.map(p => ({
          valueUuid: p.valueUuid!,
          variableHierarchyUuid: 'b74c7814-55f2-11eb-bf9e-024de1c1cc1d',
        }));
      });

    const addForm = async () => {
      try {
        addFormLoading.value = true;
        await server.addForm({
          wordUuid: word.uuid,
          formSpelling: newFormSpelling.value,
          properties: properties.value,
        });
        actions.showSnackbar(
          `Successfully added ${newFormSpelling.value} to ${word.word}`
        );
        reload && reload();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error adding new form. Please try again.',
          err as Error
        );
      } finally {
        addFormLoading.value = false;
      }
    };

    return {
      formComplete,
      newFormSpelling,
      formAlreadyExists,
      addForm,
      addFormLoading,
      setProperties,
      partOfSpeechPreselects,
    };
  },
});
